import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/ui/button";
import { useAcoes, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { confirmar } from "@/componentes/pergunta.tsx";
import { escolherCaso, montarRoteiro } from "./roteiro.ts";
import { apontar, desmontar, esconder, esperar, montar } from "./ponteiro.ts";

/**
 * A demonstração guiada da mesa.
 *
 * Uma seta anda pela tela, um HUD explica em português o que está acontecendo e
 * a mesa vai sozinha de Atendimentos até a petição nascendo dos fatos. Serve
 * para quem abre o CRM pela primeira vez e para o Felipe mostrar o sistema sem
 * ter de decorar por onde começar.
 *
 * Três regras que o desenho inteiro obedece:
 *
 * 1. **Nada grava.** A demonstração NAVEGA (a navegação mora na URL, e ler uma
 *    tela é leitura) e semeia o rascunho local. Não clica em botão nenhum, e o
 *    atendimento que ela abre é escolhido para nem a marcação de lida escrever
 *    uma linha — veja `escolherCaso`.
 * 2. **A pessoa sai quando quiser**, por quatro portas: o botão do HUD, Esc, o
 *    primeiro toque dela em qualquer lugar da mesa e o desmonte do componente.
 *    As quatro caem no mesmo `sair`, então não existe meia limpeza.
 * 3. **Singleton, com API imperativa**, no molde de `componentes/pergunta.tsx`: quem
 *    chama é uma ação (um botão em Boas-vindas, um link com `?demonstracao=1`),
 *    não um componente com estado — e dois ponteiros na tela ao mesmo tempo
 *    seriam dois donos do mesmo gesto.
 */

const DEMORA = 3200;
/* O tempo entre mandar a mesa para outro lugar e apontar o alvo: a seta precisa
   medir o elemento DEPOIS que o React o desenhou, senão aponta onde ele estava. */
const DESENHO = 420;

let publicar = null;

/** Começa a demonstração de qualquer lugar. Sem o componente montado, não faz nada. */
export function comecarDemonstracao() {
  publicar?.();
}

/** Monte uma vez, na casca. É a demonstração da mesa inteira. */
export default function Demonstracao() {
  const { mesa } = useEstado();
  const acoes = useAcoes();
  const nav = useNavegacao();
  const navegar = useNavigate();
  const local = useLocation();
  const [busca] = useSearchParams();
  const [hud, setHud] = useState(null);

  /* Espelhos: o laço é uma função async longa, e o `nav` que ela pegou no começo
     envelhece a cada passo — `ajustar` guardado por closure apagaria a query que
     o passo anterior acabou de pôr. É o mesmo motivo do `navRef` de estado.jsx. */
  const navRef = useRef(nav);
  navRef.current = nav;
  const localRef = useRef(local);
  localRef.current = local;

  const rodando = useRef(false);
  const aborto = useRef(null);
  const volta = useRef("/atendimentos");
  /* Só limpa o compositor se a demonstração escreveu nele: sair antes do passo
     de digitar não pode apagar o rascunho de quem estava trabalhando. */
  const semeou = useRef(false);

  const sair = useCallback(() => {
    if (!rodando.current) return;
    rodando.current = false;
    aborto.current?.abort();
    aborto.current = null;
    desmontar();
    setHud(null);
    /* O exemplo digitado é da demonstração, não da pessoa: sai com ela. */
    if (semeou.current) acoes.escreverNoRascunho("");
    semeou.current = false;
    navegar(volta.current, { replace: true });
  }, [acoes, navegar]);

  const rodar = useCallback(async () => {
    if (rodando.current) return;
    /* Marcado antes do `await`: sem isto, dois disparos seguidos abrem duas
       caixas de confirmação e dois laços sobre o mesmo ponteiro. */
    rodando.current = true;

    const casoId = escolherCaso(mesa, navRef.current.casoId);
    /* O endereço de volta é guardado sem o `?demonstracao=1`, senão sair caía no
       endereço que recomeça a demonstração. */
    const query = new URLSearchParams(localRef.current.search);
    query.delete("demonstracao");
    volta.current = localRef.current.pathname + (query.toString() ? `?${query}` : "");

    /* Sem caso a demonstração não recusa mais: passeia pelas áreas da mesa, e
       o fim diz como ver o caso por dentro. O setor novo é o que mais precisa
       dela, e era justamente o que ficava sem. */
    const ok = await confirmar({
      titulo: "Ver a demonstração?",
      texto: casoId
        ? "A mesa passeia sozinha por um atendimento de verdade e escreve um exemplo no campo de mensagem — se você tem rascunho ali, ele se perde. Nada é enviado a cliente nenhum, e o Esc sai a qualquer hora."
        : "A mesa passeia sozinha pelas áreas principais. Nada é gravado nem enviado, e o Esc sai a qualquer hora.",
      acao: "Começar",
    });
    if (!ok) {
      rodando.current = false;
      navegar(volta.current, { replace: true });
      return undefined;
    }

    const passos = montarRoteiro(casoId);
    const controle = new AbortController();
    aborto.current = controle;
    montar();

    for (let i = 0; i < passos.length; i++) {
      if (controle.signal.aborted) return undefined;
      const passo = passos[i];
      setHud({ titulo: passo.titulo, dica: passo.dica, i: i + 1, total: passos.length });

      if (passo.ir) navRef.current.ir(passo.ir);
      if (passo.abrirCaso) navRef.current.irParaCaso(casoId);
      if (passo.query) navRef.current.ajustar(passo.query);
      if (passo.digitar) {
        acoes.escreverNoRascunho(passo.digitar);
        semeou.current = true;
      }

      await esperar(DESENHO);
      if (controle.signal.aborted) return undefined;
      const alvo = passo.alvo ? document.querySelector(passo.alvo) : null;
      if (alvo) {
        /* O alvo pode estar abaixo da dobra (a ficha mora no pé da coluna): a
           seta na borda dizendo "é lá embaixo" não mostra nada. `nearest` rola
           o mínimo, e só o contêiner dele. */
        alvo.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
        await esperar(260);
        if (controle.signal.aborted) return undefined;
      }
      if (alvo) apontar(alvo, passo.titulo);
      else esconder();

      await esperar(passo.demora ?? DEMORA);
    }

    if (controle.signal.aborted) return undefined;
    esconder();
    setHud({
      titulo: casoId ? "É essa a mesa" : "Por dentro de um caso",
      dica: casoId
        ? "Do primeiro oi ao número do processo voltando para o cliente. O “?” de cada tela leva ao capítulo dela."
        : mesa.length
          ? /* Há caso, mas todos com mensagem nova: abrir um marcaria a leitura
               (ver `escolherCaso`). Quem abre é a pessoa; aí a demonstração usa esse. */
            "Abra um atendimento e chame de novo: a demonstração mostra o caso aberto por dentro."
          : "Com o primeiro atendimento na lista, chame de novo e a demonstração abre o caso por dentro.",
      i: passos.length,
      total: passos.length,
      fim: true,
    });
    return undefined;
  }, [acoes, mesa, navegar]);

  /* Sempre a versão de agora do laço, sem pôr `mesa` nas dependências do efeito
     que dispara — a lista se recarrega a cada evento do SSE, e a demonstração
     recomeçaria do zero no meio de si mesma. */
  const rodarRef = useRef(rodar);
  rodarRef.current = rodar;

  useEffect(() => {
    publicar = () => rodarRef.current();
    return () => {
      publicar = null;
    };
  }, []);

  /* `?demonstracao=1` é endereço: dá para mandar por mensagem ("abre isto e vê
     como funciona") e é o que um botão em qualquer tela precisa saber montar. */
  const pedida = busca.get("demonstracao") === "1";
  useEffect(() => {
    if (pedida) rodarRef.current();
  }, [pedida]);

  /* Desmontar a casca no meio do passeio não pode deixar a seta na tela. */
  useEffect(() => desmontar, []);

  useEffect(() => {
    if (!hud) return undefined;
    const tecla = (e) => {
      if (e.key === "Escape") sair();
    };
    /*
     * O primeiro toque da pessoa encerra: quem pegou o mouse quer operar, não
     * assistir. `isTrusted` é o que separa a mão dela da mão do roteiro — e por
     * isso o botão do HUD precisa da exceção, senão o clique nele fecharia a
     * demonstração pelo caminho errado, sem passar pelo `sair`.
     */
    const toque = (e) => {
      if (e.isTrusted && !e.target?.closest?.("[data-demo]")) sair();
    };
    document.addEventListener("keydown", tecla, true);
    document.addEventListener("pointerdown", toque, true);
    return () => {
      document.removeEventListener("keydown", tecla, true);
      document.removeEventListener("pointerdown", toque, true);
    };
  }, [hud, sair]);

  if (!hud) return null;

  /* A pílula é centralizada por `mx-auto`, e não por `translateX(-50%)`: o
     `vFade` anima `transform`, e enquanto ele roda o deslocamento do centro
     sumia — ela nascia à direita e pulava para o lugar no fim da animação. */
  return (
    <div
      data-demo=""
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-4 z-[105] mx-auto flex w-max max-w-[min(94vw,760px)] max-[759px]:bottom-[calc(76px+env(safe-area-inset-bottom))] max-[759px]:w-[94vw] max-[759px]:flex-wrap animate-[vFade_var(--t-curta)_var(--e-entra)_both] items-center gap-[11px] rounded-[18px] border border-terra/50 bg-foreground/94 px-[13px] py-[9px] text-apoio leading-[1.35] text-creme shadow-[0_16px_40px_color-mix(in oklab, var(--color-foreground) 40%, transparent)] backdrop-blur-[8px]"
    >
      <span className={`size-[9px] flex-none rounded-full ${hud.fim ? "bg-verde-vivo" : "bg-primary"}`} />
      <b className="flex-none tracking-[-.01em] whitespace-nowrap [font-weight:650]">{hud.titulo}</b>
      {/* O único pedaço elástico: encolhe com reticências antes de empurrar o
          contador e o botão para a linha de baixo. */}
      {/* A dica QUEBRA em vez de virar reticências: era uma pílula de uma linha,
          e a frase do passo — que é o que a pessoa veio ler — sumia atrás de "…"
          já na metade, no computador; no celular sobrava só o título. No
          telefone ela desce para a linha de baixo, inteira. */}
      <span className="min-w-0 flex-[0_1_auto] text-creme/72 max-[759px]:order-last max-[759px]:basis-full">
        {hud.dica}
      </span>
      <span className="flex-none font-mono text-selo whitespace-nowrap text-creme/50">
        {hud.i}/{hud.total}
      </span>
      <Button
        size="xs"
        onClick={sair}
        className="h-auto flex-none rounded-full border-transparent bg-creme/14 px-[11px] py-[5px] text-selo whitespace-nowrap text-creme [font-weight:650] hover:bg-creme/24"
      >
        {hud.fim ? "Fechar" : "Sair da demonstração"}
      </Button>
    </div>
  );
}
